import { useState, useEffect } from 'react';
import {
  Palette, Save, Loader2, AlertCircle, CheckCircle,
  Monitor, Layers, RotateCw, Settings, Eye, Edit3, X
} from 'lucide-react';
import { Switch, ColorPickerPanel, MessagePlugin } from 'tdesign-react';
import ProductImageUploader from '@/user/components/ProductImageUploader';
import { cardTemplateApi, tokenStorage } from '@/user/api';
import type { CardTemplateVO } from '@/types/eCard';
import { PreviewableImage } from './components/ImagePreview';

const presetColors = [
  '#1c5bff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
  '#13c2c2', '#eb2f96', '#2f54eb', '#1890ff', '#a0d911',
];

const defaultTemplate: CardTemplateVO = {
  templateName: '',
  logo: '',
  status: 1,
  tenantId: 0,
  colorConfig: {
    buttonColor: '#1c5bff',
    dividerColor: '#eeeeee',
    textHighlightColor: '#000000',
    textNormalColor: '#666666',
    themeColor: '#1c5bff',
  },
  navBarConfig: {
    backgroundColor: '#ffffff',
    textColor: '#333333',
  },
  tabbarConfig: {
    highlightColor: '#1c5bff',
    normalColor: '#999999',
  },
  watermarkConfig: {
    angle: -15,
    textColor: '#ff0000',
  },
  personalCenterBg: '',
};

function ColorPickerField({
  value,
  onChange,
  label,
  description
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
      <div className="flex items-center justify-between mb-3">
        <div>
          <label className="block text-sm font-semibold text-gray-800">{label}</label>
          {description && <span className="text-xs text-gray-400">{description}</span>}
        </div>
        {value && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded border border-gray-200">
            <div className="w-3 h-3 rounded border border-gray-200" style={{ backgroundColor: value }} />
            <span className="text-xs font-mono font-medium text-gray-600">{value.toUpperCase()}</span>
          </div>
        )}
      </div>
      <ColorPickerPanel
        value={value}
        onChange={(val: string) => onChange(val)}
        format="HEX"
        clearable
        borderless
        swatchColors={presetColors}
        showPrimaryColorPreview
      />
    </div>
  );
}

function ColorDisplay({ label, value, description }: { label: string; value: string; description?: string }) {
  return (
    <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg border border-gray-200 shadow-sm shrink-0"
          style={{ backgroundColor: value }}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-700">{label}</div>
          <div className="text-xs font-mono text-gray-500">{value?.toUpperCase()}</div>
          {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
        </div>
      </div>
    </div>
  );
}

function DisplayRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-800 font-medium">{value}</span>
    </div>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
  action
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#1c5bff]" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function ThemeConfiguration() {
  const [template, setTemplate] = useState<CardTemplateVO>(defaultTemplate);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await cardTemplateApi.getTemplate();
      if (res.code === 200 && res.data) {
        setTemplate({
          ...defaultTemplate,
          ...res.data,
          colorConfig: { ...defaultTemplate.colorConfig, ...res.data.colorConfig },
          navBarConfig: { ...defaultTemplate.navBarConfig, ...res.data.navBarConfig },
          tabbarConfig: { ...defaultTemplate.tabbarConfig, ...res.data.tabbarConfig },
          watermarkConfig: { ...defaultTemplate.watermarkConfig, ...res.data.watermarkConfig },
        });
        setHasData(true);
        setIsEditing(false);
      } else {
        setHasData(false);
        setIsEditing(true);
      }
    } catch {
      setHasData(false);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template.templateName.trim()) {
      setError('请输入模板名称');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const tenantInfo = tokenStorage.getTenantInfo();
      const submitData: CardTemplateVO = {
        ...template,
        tenantId: tenantInfo?.id || 0,
      };
      const res = template.id
        ? await cardTemplateApi.saveTemplate(submitData)
        : await cardTemplateApi.saveTemplate(submitData);
      if (res.code === 200 && res.data) {
        setTemplate(res.data);
        setHasData(true);
        setIsEditing(false);
        setMessage(template.id ? '更新成功' : '保存成功');
        setTimeout(() => setMessage(''), 3000);
        MessagePlugin.success(template.id ? '更新成功' : '保存成功');
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err: any) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasData) {
      setIsEditing(false);
    } else {
      loadData();
      setIsEditing(false);
    }
  };

  const updateTemplate = (path: string, value: any) => {
    setTemplate((prev: any) => {
      const next: any = { ...prev };
      const keys = path.split('.');
      let current = next;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1c5bff] animate-spin" />
      </div>
    );
  }

  const showEditActions = isEditing;
  const showReadOnlyActions = !isEditing && hasData;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Palette className="w-6 h-6 text-[#1c5bff]" />
          主题配置
        </h1>
        {showReadOnlyActions && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 text-sm"
          >
            <Edit3 className="w-4 h-4" />
            编辑
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {message}
        </div>
      )}

      <div className="space-y-4">
        {/* 基础信息 */}
        <SectionCard title="基础信息" icon={Settings}>
          {!isEditing ? (
            <div className="divide-y divide-gray-50">
              <DisplayRow label="模板名称" value={template.templateName || '-'} />
              <DisplayRow label="企业Logo" value={template.logo ? (
                <PreviewableImage src={template.logo} alt="logo" className="h-12 rounded" />
              ) : '-'} />
              <DisplayRow label="状态" value={
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${template.status === 1 ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                  {template.status === 1 ? '已启用' : '已停用'}
                </span>
              } />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">模板名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={template.templateName}
                  onChange={(e) => updateTemplate('templateName', e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="如：默认蓝色主题"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">企业Logo</label>
                <ProductImageUploader
                  value={template.logo}
                  onChange={(val) => updateTemplate('logo', val)}
                  simple
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
                <div className="flex items-center gap-4">
                  <Switch
                    value={template.status === 1}
                    onChange={(v) => updateTemplate('status', v ? 1 : 0)}
                  />
                  <span className="text-sm text-gray-600">
                    {template.status === 1 ? '已启用' : '已停用'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* 颜色配置 */}
        <SectionCard title="颜色配置" icon={Palette}>
          {!isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <ColorDisplay label="主题色" value={template.colorConfig.themeColor} description="主要强调色" />
              <ColorDisplay label="按钮颜色" value={template.colorConfig.buttonColor} description="按钮背景色" />
              <ColorDisplay label="文字与名片标题色" value={template.colorConfig.textHighlightColor} description="强调文字颜色" />
              <ColorDisplay label="文字常规色" value={template.colorConfig.textNormalColor} description="普通文字颜色" />
              <ColorDisplay label="分割线颜色" value={template.colorConfig.dividerColor} description="线条和边框颜色" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <ColorPickerField label="主题色" description="主要强调色" value={template.colorConfig.themeColor} onChange={(v) => updateTemplate('colorConfig.themeColor', v)} />
              <ColorPickerField label="按钮颜色" description="按钮背景色" value={template.colorConfig.buttonColor} onChange={(v) => updateTemplate('colorConfig.buttonColor', v)} />
              <ColorPickerField label="文字与名片标题色" description="强调文字颜色" value={template.colorConfig.textHighlightColor} onChange={(v) => updateTemplate('colorConfig.textHighlightColor', v)} />
              <ColorPickerField label="文字常规色" description="普通文字颜色" value={template.colorConfig.textNormalColor} onChange={(v) => updateTemplate('colorConfig.textNormalColor', v)} />
              <ColorPickerField label="分割线颜色" description="线条和边框颜色" value={template.colorConfig.dividerColor} onChange={(v) => updateTemplate('colorConfig.dividerColor', v)} />
            </div>
          )}
        </SectionCard>

        {/* 导航栏配置 */}
        {/* <SectionCard title="导航栏配置" icon={Monitor}>
          {!isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ColorDisplay label="导航栏背景色" value={template.navBarConfig.backgroundColor} description="顶部导航背景" />
              <ColorDisplay label="导航栏文字色" value={template.navBarConfig.textColor} description="导航文字颜色" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ColorPickerField label="导航栏背景色" description="顶部导航背景" value={template.navBarConfig.backgroundColor} onChange={(v) => updateTemplate('navBarConfig.backgroundColor', v)} />
              <ColorPickerField label="导航栏文字色" description="导航文字颜色" value={template.navBarConfig.textColor} onChange={(v) => updateTemplate('navBarConfig.textColor', v)} />
            </div>
          )}
        </SectionCard> */}

        {/* 底部Tabbar */}
        <SectionCard title="tabbar与个人背景图（宽度需要适配微信小程序，高度不限）" icon={Layers}>
          {!isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorDisplay label="普通颜色" value={template.tabbarConfig.normalColor} description="未选中状态" />
                <ColorDisplay label="高亮颜色" value={template.tabbarConfig.highlightColor} description="选中状态" />
              </div>
              <div className="flex items-center justify-between py-2 border-t border-gray-50 pt-4">
                <span className="text-sm text-gray-500">个人中心背景图</span>
                {template.personalCenterBg ? (
                  <PreviewableImage src={template.personalCenterBg} alt="bg" className="w-16 h-10 rounded object-cover" />
                ) : (
                  <span className="text-sm text-gray-400">-</span>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorPickerField label="普通颜色" description="未选中状态" value={template.tabbarConfig.normalColor} onChange={(v) => updateTemplate('tabbarConfig.normalColor', v)} />
                <ColorPickerField label="高亮颜色" description="选中状态" value={template.tabbarConfig.highlightColor} onChange={(v) => updateTemplate('tabbarConfig.highlightColor', v)} />
              </div> 
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">个人中心背景图</label>
                <ProductImageUploader
                  value={template.personalCenterBg}
                  onChange={(val) => updateTemplate('personalCenterBg', val)}
                  simple
                />
              </div>
            </div>
          )}
        </SectionCard>

        {/* 水印配置 */}
        <SectionCard title="水印配置" icon={RotateCw}>
          {!isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ColorDisplay label="水印文字颜色" value={template.watermarkConfig.textColor} description={`水印显示颜色，倾斜角度为${template.watermarkConfig.angle}°`} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50 space-y-2">
                <label className="block text-sm font-semibold text-gray-800">倾斜角度</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="-45"
                    max="45"
                    value={template.watermarkConfig.angle}
                    onChange={(e) => updateTemplate('watermarkConfig.angle', Number(e.target.value))}
                    className="flex-1 accent-[#1c5bff]"
                  />
                  <span className="text-sm font-medium text-gray-700 w-12 text-right">{template.watermarkConfig.angle}°</span>
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>-45°</span>
                  <span>0°</span>
                  <span>45°</span>
                </div>
              </div>
              <ColorPickerField label="水印文字颜色" description="水印显示颜色" value={template.watermarkConfig.textColor} onChange={(v) => updateTemplate('watermarkConfig.textColor', v)} />
            </div>
          )}
        </SectionCard>

        {/* 效果预览 */}
        {/* <SectionCard title="效果预览" icon={Eye}>
          <div className="rounded-xl overflow-hidden border border-gray-200">
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: template.navBarConfig.backgroundColor, color: template.navBarConfig.textColor }}
            >
              <span className="font-bold">{template.templateName || '模板名称'}</span>
              <span className="text-sm">导航栏预览</span>
            </div>
            <div className="p-4 space-y-3 bg-white">
              <div className="flex items-center gap-3">
                {template.logo && (
                  <img src={template.logo} alt="logo" className="w-10 h-10 rounded object-cover" />
                )}
                <span className="font-medium" style={{ color: template.colorConfig.textNormalColor }}>
                  {template.templateName || '企业名称'}
                </span>
              </div>
              <div className="h-px" style={{ backgroundColor: template.colorConfig.dividerColor }} />
              <div className="space-y-2">
                <div className="text-sm" style={{ color: template.colorConfig.textNormalColor }}>
                  文字常规色效果展示
                </div>
                <div className="text-sm font-bold" style={{ color: template.colorConfig.textHighlightColor }}>
                  文字高亮色效果展示
                </div>
              </div>
              <button
                className="w-full py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: template.colorConfig.buttonColor }}
              >
                按钮预览
              </button>
            </div>
            <div
              className="px-4 py-3 flex items-center justify-around"
              style={{ borderTop: `1px solid ${template.colorConfig.dividerColor}` }}
            >
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-5 h-5 rounded bg-gray-200" />
                <span className="text-xs" style={{ color: template.tabbarConfig.normalColor }}>首页</span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-5 h-5 rounded bg-gray-200" />
                <span className="text-xs" style={{ color: template.tabbarConfig.normalColor }}>发现</span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-5 h-5 rounded" style={{ backgroundColor: template.tabbarConfig.highlightColor }} />
                <span className="text-xs font-medium" style={{ color: template.tabbarConfig.highlightColor }}>我的</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">主题色</div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: template.colorConfig.themeColor }} />
                <span className="text-gray-700">{template.colorConfig.themeColor}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">按钮颜色</div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: template.colorConfig.buttonColor }} />
                <span className="text-gray-700">{template.colorConfig.buttonColor}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">水印角度</div>
              <div className="text-gray-700">{template.watermarkConfig.angle}°</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">状态</div>
              <div className="text-gray-700">{template.status === 1 ? '启用' : '停用'}</div>
            </div>
          </div>
        </SectionCard> */}

        {/* 底部操作区 */}
        {showEditActions && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 rounded-xl shadow-sm p-4 flex items-center justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {template.id ? '更新模板' : '保存模板'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
